/**
 * RevealCelebration — Mutual Reveal Celebration (FE-016 / DES-005)
 *
 * Brief (2s) overlay shown when mutual reveal is accepted.
 * Features CSS-only confetti/sparkle animation (no library).
 * Text: "You've both chosen to reveal!"
 * Fades out automatically after 2 seconds.
 */

import { type Component, createSignal, createEffect, onCleanup, Show } from "solid-js";
import type { StageManager } from "../services/stage-manager";
import styles from "./RevealCelebration.module.css";

export interface RevealCelebrationProps {
  stageManager: StageManager;
}

/** Inline SVG: 4-pointed sparkle */
function SparkleIcon(props: { class?: string }) {
  return (
    <svg
      class={props.class}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M12 2L13.5 9L20 7L14.5 12L20 17L13.5 15L12 22L10.5 15L4 17L9.5 12L4 7L10.5 9L12 2Z"
        fill="white"
        opacity="0.9"
      />
    </svg>
  );
}

const CELEBRATION_DURATION_MS = 2000;

const RevealCelebration: Component<RevealCelebrationProps> = (props) => {
  const [isVisible, setIsVisible] = createSignal(false);
  let hideTimer: ReturnType<typeof setTimeout> | null = null;

  // Show celebration when reveal is accepted
  createEffect(() => {
    const state = props.stageManager.revealState();
    if (state === "accepted") {
      setIsVisible(true);

      // Auto-hide after duration
      if (hideTimer) clearTimeout(hideTimer);
      hideTimer = setTimeout(() => {
        setIsVisible(false);
      }, CELEBRATION_DURATION_MS);
    }
  });

  onCleanup(() => {
    if (hideTimer) clearTimeout(hideTimer);
  });

  return (
    <Show when={isVisible()}>
      <div
        class={styles.overlay}
        role="status"
        aria-live="assertive"
        aria-label="Both people chose to reveal"
      >
        {/* Confetti particles */}
        <div class={styles.confettiContainer} aria-hidden="true">
          <div class={`${styles.particle} ${styles.particle1}`} />
          <div class={`${styles.particle} ${styles.particle2}`} />
          <div class={`${styles.particle} ${styles.particle3}`} />
          <div class={`${styles.particle} ${styles.particle4}`} />
          <div class={`${styles.particle} ${styles.particle5}`} />
          <div class={`${styles.particle} ${styles.particle6}`} />
          <div class={`${styles.particle} ${styles.particle7}`} />
          <div class={`${styles.particle} ${styles.particle8}`} />
        </div>

        {/* Celebration text with sparkle decorations */}
        <div class={styles.textWrapper}>
          <SparkleIcon class={styles.sparkleLeft} />
          <p class={styles.text}>You've both chosen to reveal!</p>
          <SparkleIcon class={styles.sparkleRight} />
        </div>
      </div>
    </Show>
  );
};

export default RevealCelebration;
